import { togglePropertyStripMode } from '../../canvas/commands/ManipulationCommands';
import { repeat } from '../../canvas/commands/SelectionCommands';
import { LockablePropertyStripLayout } from '../../canvas/properties/LockablePropertyStripLayout';
import { PropertyStripLayout } from '../../canvas/properties/PropertyStripLayout';
import { lockAndMoreLayout } from '../../canvas/properties/TextPropertyStripParts';
import { Connector } from './Connector';
import { ConnectorCapStyle, ConnectorMode, ConnectorStrokeStyle } from './ConnectorEnums';

const capSelector = (side: 'start' | 'end') =>
    PropertyStripLayout<Connector>((d, s, o, w, a) => {

        const menu = w.createButtonStrip({
            text: `${side === 'start' ? 'Start' : 'End'} Cap`
        });

        // Icon Transform:
        const it = side === 'start' ? '@[1,1,180]' : '';

        const tm = (label: string, image: string, value: number) => ({ label, image, value });
        const opts = [
            tm('None', `deqo://icons/d2/cap-none${it}`, ConnectorCapStyle.none),
            tm('Spot', `deqo://icons/d2/cap-dot${it}`, ConnectorCapStyle.spot),
            tm('Solid Arrow', `deqo://icons/d2/cap-arrow${it}`, ConnectorCapStyle.solidArrow),
            tm('Line Arrow', `deqo://icons/d2/cap-arrow-line${it}`, ConnectorCapStyle.lineArrow),
        ];

        const sync = () => {
            const cap = side === 'start' ? a.get('startCap') : a.get('endCap');
            const item = opts.find(x => x.value === cap);
            if (!item) return;
            menu.update({
                image: item.image,
                text: item.label,
            });
        };

        const items = opts.map(x =>
            w.createButton({
                image: x.image,
                text: x.label,
                onAction() {
                    o.update(u => {
                        u.rolling(side === 'start' ? a.keyFor('startCap') : a.keyFor('endCap'));
                        if (side === 'start') a.set('startCap', x.value);
                        else a.set('endCap', x.value);
                    });
                    sync();
                }
            })
        );

        menu.update({ items });
        sync();

        return [menu];

    });

const styleSelector = PropertyStripLayout<Connector>((d, s, o, w, a) => {

    const menu = w.createButtonStrip({
        text: 'Line Style',
        image: 'deqo://icons/d2/line-style',
    });

    const tm = (label: string, image: string, value: number) => ({ label, image, value });
    const opts = [
        tm('Solid', 'deqo://icons/d2/line-style-solid', ConnectorStrokeStyle.solid),
        tm('Dashed', 'deqo://icons/d2/line-style-dash', ConnectorStrokeStyle.dashed),
        tm('Dotted', 'deqo://icons/d2/line-style-dotted', ConnectorStrokeStyle.dotted),
        // tm('Cool', ConnectorStrokeStyle.cheveron),
    ];

    const items = opts.map(x =>
        w.createButton({
            image: x.image,
            text: x.label,
            onAction() {
                o.update(u => {
                    u.rolling(a.keyFor('strokeStyle'));
                    a.set('strokeStyle', x.value);
                });
                menu.update({ text: x.label });
            }
        })
    );

    menu.update({ items });
    return [menu];

});

const bendSelector = PropertyStripLayout<Connector>((d, s, o, w, a) => {

    const menu = w.createButtonStrip({
        text: 'Bend'
    });

    const tm = (label: string, image: string, value: number) => ({ label, image, value });
    const opts = [
        tm('Direct', 'deqo://icons/d2/line-direct', ConnectorMode.direct),
        tm('Smooth', 'deqo://icons/d2/line-bend-smooth', ConnectorMode.smooth),
        tm('Manhattan', 'deqo://icons/d2/line-bend-manhatten', ConnectorMode.manhattan),
    ];

    const sync = () => {
        const mode = a.get('mode');
        const item = opts.find(x => x.value === mode);
        if (!item) return;
        menu.update({
            image: item.image,
            text: item.label,
        });
    };

    const items = opts.map(x =>
        w.createButton({
            image: x.image,
            text: x.label,
            onAction() {
                o.update(u => {
                    u.rolling(a.keyFor('mode'));
                    a.set('mode', x.value);
                });
                menu.update({});
                sync();
            }
        })
    );

    menu.update({ items });
    sync();

    return [menu];

});

export const connectorPropertyLayout = LockablePropertyStripLayout<Connector>((d, s, o, w, a) => [
    //
    // Edit Text
    w.createButton({
        name: 'editText',
        image: 'deqo://icons/i8/text',
        text: 'Edit Text',
        onAction() {
            d(togglePropertyStripMode);
        },
    }),
    //
    // Divider
    w.createDivide(),
    //
    // Color
    w.createColorSelector({
        autoSelect: true,
        groups: true,
        value: a.get('color'),
        onSelect() {
            o.update(u => {
                u.rolling(a.keyFor('color'));
                a.set('color', this.value);
            });
        }
    }),
    //
    // Stroke
    w.createSlider({
        value: a.get('strokeWidth'),
        min: 1,
        image: 'deqo://icons/d2/line-weight',
        text: 'Stroke Width',
        onSelect() {
            o.update(u => {
                u.rolling(a.keyFor('strokeWidth'));
                a.set('strokeWidth', this.value);
            });
        }
    }),
    ...styleSelector(d, s, o, w, a),
    ...capSelector('start')(d, s, o, w, a),
    ...capSelector('end')(d, s, o, w, a),
    ...bendSelector(d, s, o, w, a),
    //
    // Divider
    w.createDivide(),
    ...(a.count === 1 ? [
        //
        // Create Another
        w.createButton({
            image: 'deqo://icons/hero/refresh',
            text: 'Create Another',
            onAction() {
                d(repeat);
            }
        })
    ] : []),
    //
    // Divider
    w.createDivide(),
    //
    // More
    ...lockAndMoreLayout(d, s, o, w, a),
]);
